import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiTarget, 
  FiEye, 
  FiHeart, 
  FiUsers, 
  FiUser,
  FiShield, 
  FiTrendingUp,
  FiAward,
  FiStar,
  FiArrowRight,
  FiArrowLeft,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import ImageCarousel from '../components/ImageCarousel';
import Chatbot from '../components/Chatbot';
import '../styles/pages/About.css';
import '../styles/animations/ScrollAnimations.css';

// Import benefit logos
import sssLogo from '../images/SSS LOGO.png';
import philhealthLogo from '../images/PHILHEALTH LOGO.png';
import pagibigLogo from '../images/PAGIBIG LOGO.png';
import thirteenthMonthLogo from '../images/13TH MONTH PAY LOGO.png';
import incentiveLogo from '../images/INCENTIVE LOGO.png';
import compensationLogo from '../images/COMPENSATION LOGO.jpg';

// Import executive member photos
import miguelRamirez from '../images/MEDC OFFICE EMPLOYEES/MEDC EXECUTIVES/miguel ramirez_chairman.png';
import avelinoHermoso from '../images/MEDC OFFICE EMPLOYEES/MEDC EXECUTIVES/avelino hermoso_general manager.png';
import kristelleLatayada from '../images/MEDC OFFICE EMPLOYEES/MEDC EXECUTIVES/kristelle latayada_executive secretary.png';
import timLatorre from '../images/MEDC OFFICE EMPLOYEES/MEDC EXECUTIVES/tim latorre_contracts officer.png';
import dianneBaoilan from '../images/MEDC OFFICE EMPLOYEES/MEDC EXECUTIVES/dianne baoilan_contracts officer.png';

// Import mission/core values images
import peopleHelpingPeopleImg from '../images/MEDC CORE VALUES IMAGES/PEOPLE HELPING PEOPLE.png';
import increasingMembersImg from '../images/MEDC CORE VALUES IMAGES/INCREASING MEMBERS.png';
import goodGovernanceImg from '../images/MEDC CORE VALUES IMAGES/GOOD GOVERNANCE.png';
import memberConfidenceImg from '../images/MEDC CORE VALUES IMAGES/MEMBER CONFIDENCE.png';
import hittingSmartGoalsImg from '../images/MEDC CORE VALUES IMAGES/HITTING SMART GOALS.png';

const About = () => {
  const { t } = useLanguage();
  const sectionsRef = useRef([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showClickHint, setShowClickHint] = useState(true);
  const lastInteractionRef = useRef(Date.now());

  const storyCarouselSlides = [
    {
      id: 'story',
      title: 'Our Story',
      icon: <FiHeart />
    },
    {
      id: 'vision',
      title: 'Our Vision',
      icon: <FiEye />
    },
    {
      id: 'mission',
      title: 'Our Mission',
      icon: <FiTarget />
    },
    {
      id: 'benefits',
      title: 'Employee Benefits',
      icon: <FiAward />
    },
    {
      id: 'requirements',
      title: 'Selection Requirements',
      icon: <FiCheckCircle />
    },
    {
      id: 'assurances',
      title: 'Client Assurances',
      icon: <FiShield />
    }
  ];

  // Hover state for pausing carousel
  const [isHovered, setIsHovered] = useState(false);

  // Auto-advance after 10 seconds of inactivity (pauses on hover)
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      if (isHovered) return; // Don't advance when hovered
      const timeSinceLastInteraction = Date.now() - lastInteractionRef.current;
      if (timeSinceLastInteraction >= 10000) {
        setActiveSlide((prev) => (prev + 1) % storyCarouselSlides.length);
        lastInteractionRef.current = Date.now();
      }
    }, 1000);
    return () => clearInterval(checkInactivity);
  }, [storyCarouselSlides.length, isHovered]);

  // Hide click hint after first interaction
  const handleSlideChange = (newSlide) => {
    setActiveSlide(newSlide);
    lastInteractionRef.current = Date.now();
    setShowClickHint(false);
  };

  const goToNextSlide = () => {
    handleSlideChange((activeSlide + 1) % storyCarouselSlides.length);
  };

  const goToPrevSlide = () => {
    handleSlideChange((activeSlide - 1 + storyCarouselSlides.length) % storyCarouselSlides.length);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('[class*="scroll-"]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const benefits = [
    { name: 'Updated SSS Remittances', logo: sssLogo },
    { name: 'PHIC (PhilHealth)', logo: philhealthLogo },
    { name: 'Pag-IBIG Fund', logo: pagibigLogo },
    { name: '13th Month Pay', logo: thirteenthMonthLogo },
    { name: 'Five (5) Days Incentive Leave with Pay', logo: incentiveLogo },
    { name: 'Employees Compensation', logo: compensationLogo }
  ];

  const clearances = [
    { title: 'Local & National Clearances', items: ['Barangay Clearance', 'Police Clearance', 'NBI Clearance'] },
    { title: 'Certificates from Accredited Clinics', items: ['Drug Test', 'Psycho-Neuro Exam', 'Physical Check-up'] }
  ];

  const assurances = [
    'Surety and Performance Bond',
    'Competent Supervision',
    'Well Trained Personnel',
    'Immediate Attention and Due Process on Complaints',
    'Replacement of Personnel Who Fell Short of Expectations',
    'Support Relievers (Extra Personnel) to Take Over in Absence of Assigned Employee'
  ];

  const executives = [
    { name: 'Miguel B. Ramirez', position: 'Chairman', image: miguelRamirez },
    { name: 'Avelino A. Hermoso', position: 'General Manager', image: avelinoHermoso },
    { name: 'Kristelle Laigh Latayada', position: 'Executive Secretary', image: kristelleLatayada },
    { name: 'Tim Jimmil Latorre', position: 'Contracts Officer', image: timLatorre },
    { name: 'Dianne Baoilan', position: 'Contracts Officer', image: dianneBaoilan }
  ];

  // Organization Chart data removed - now hardcoded in JSX for exact layout control

  return (
    <div className="about-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>{t('about.title')}</h1>
          <div className="breadcrumb">
            <Link to="/">{t('navbar.home')}</Link>
            <span>/</span>
            <span>{t('navbar.about')}</span>
          </div>
        </div>
      </section>

      {/* Story/Vision/Values Carousel Section */}
      <section className="about-carousel-section section">
        <div className="container">
          {/* Carousel Content */}
          <div 
            className="carousel-content-wrapper"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Our Story Slide */}
            <div className={`carousel-slide ${activeSlide === 0 ? 'active' : ''}`}>
              <div className="about-story-content">
                <div className="story-image">
                  <ImageCarousel showOverlay={true} />
                </div>
                <div className="story-text">
                  <h2>{t('about.ourStory')}</h2>
                  <p>
                    <strong>Employees' Development Cooperative (MEDC)</strong> was 
                    established on <strong>October 29, 1997</strong> and is duly registered 
                    with the Cooperative Development Authority (CDA) under Registration 
                    Number <strong>6520-11009760</strong> with CIN-0105110096.
                  </p>
                  <p>
                    Today, we have a workforce of more than <strong>2,500 personnel</strong> distributed across 
                    <strong> Luzon, Visayas, and Mindanao</strong>. With more than a decade of 
                    supervision experience, MEDC consistently adheres to the principle of 
                    pro-employee policy - giving what is due to employees.
                  </p>
                  <p>
                    MEDC has always adopted a <strong>pro-employee policy</strong> - giving 
                    what is due to our employees. We believe that taking care of our 
                    workforce results in better service delivery to our clients.
                  </p>
                  <Link to="/contact" className="btn btn-primary">
                    {t('about.partnerWithUs')} <FiArrowRight />
                  </Link>
                </div>
              </div>
            </div>

            {/* Vision Slide */}
            <div className={`carousel-slide ${activeSlide === 1 ? 'active' : ''}`}>
              <div className="vision-only-slide-content">
                <div className="vision-only-card">
                  <div className="vision-only-icon">
                    <FiEye />
                  </div>
                  <h3>Our Vision</h3>
                  <p>
                    MEDC is a strong, sustainable and viable cooperative responsive to 
                    the socio-economic needs of its members, providing competitive 
                    subsidiaries, guided by the principles of good governance, 
                    transparency and entrepreneurial cooperative values.
                  </p>
                  <div className="vision-only-tagline">
                    <span>"Unicity in Progress"</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Slide */}
            <div className={`carousel-slide ${activeSlide === 2 ? 'active' : ''}`}>
              <div className="mission-slide-content">
                <div className="mission-icon-large">
                  <FiTarget />
                </div>
                <h2>Our Mission</h2>
                <div className="mission-list-grid">
                  <div className="mission-item">
                    <img src={peopleHelpingPeopleImg} alt="People Helping People" className="mission-item-img" />
                    <span>People Helping People</span>
                  </div>
                  <div className="mission-item">
                    <img src={increasingMembersImg} alt="Increasing Members" className="mission-item-img" />
                    <span>Increasing Members</span>
                  </div>
                  <div className="mission-item">
                    <img src={goodGovernanceImg} alt="Good Governance" className="mission-item-img" />
                    <span>Good Governance</span>
                  </div>
                  <div className="mission-item">
                    <img src={memberConfidenceImg} alt="Member Confidence" className="mission-item-img" />
                    <span>Member Confidence</span>
                  </div>
                  <div className="mission-item">
                    <img src={hittingSmartGoalsImg} alt="Hitting Smart Goals" className="mission-item-img" />
                    <span>Hitting Smart Goals</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Benefits Slide */}
            <div className={`carousel-slide ${activeSlide === 3 ? 'active' : ''}`}>
              <div className="benefits-slide-content">
                <div className="slide-icon-large">
                  <FiAward />
                </div>
                <h2>Employee Benefits</h2>
                <p className="slide-subtitle">What MEDC provides to all deployed personnel</p>
                <div className="benefits-carousel-grid">
                  {benefits.map((benefit, index) => (
                    <div className="benefit-carousel-card" key={index}>
                      {benefit.logo ? (
                        <img src={benefit.logo} alt={benefit.name} className="benefit-carousel-logo" />
                      ) : (
                        <div className="benefit-carousel-icon"><FiAward /></div>
                      )}
                      <span>{benefit.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Selection Requirements Slide */}
            <div className={`carousel-slide ${activeSlide === 4 ? 'active' : ''}`}>
              <div className="requirements-slide-content">
                <div className="slide-icon-large">
                  <FiCheckCircle />
                </div>
                <h2>Employee Selection Requirements</h2>
                <p className="slide-subtitle">All employees undergo complete security measure-documents</p>
                <div className="requirements-carousel-grid">
                  {clearances.map((category, index) => (
                    <div className="requirement-carousel-card" key={index}>
                      <h4>{category.title}</h4>
                      <ul>
                        {category.items.map((item, idx) => (
                          <li key={idx}><FiCheckCircle /> {item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Client Assurances Slide */}
            <div className={`carousel-slide ${activeSlide === 5 ? 'active' : ''}`}>
              <div className="assurances-slide-content">
                <div className="slide-icon-large">
                  <FiShield />
                </div>
                <h2>Client Assurances</h2>
                <p className="slide-subtitle">Our commitment to quality service delivery</p>
                <div className="assurances-carousel-grid">
                  {assurances.map((assurance, index) => (
                    <div className="assurance-carousel-card" key={index}>
                      <FiShield className="assurance-carousel-icon" />
                      <span>{assurance}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Side Navigation Buttons */}
            <button 
              className={`carousel-nav-btn prev ${showClickHint ? 'show-hint' : ''}`}
              onClick={goToPrevSlide}
              aria-label="Previous slide"
            >
              <FiChevronLeft />
              {showClickHint && <span className="click-hint">Click</span>}
            </button>
            
            <button 
              className={`carousel-nav-btn next ${showClickHint ? 'show-hint' : ''}`}
              onClick={goToNextSlide}
              aria-label="Next slide"
            >
              <FiChevronRight />
              {showClickHint && <span className="click-hint">Click</span>}
            </button>
          </div>
        </div>
      </section>

      {/* Executive Department Section */}
      <section className="executives-section section">
        <div className="container">
          <div className="section-header scroll-fade-up">
            <h2 className="section-title">Executive Department</h2>
            <p className="section-subtitle">
              Meet the dedicated leaders guiding MEDC towards excellence
            </p>
          </div>
          {/* Top Row - Chairman and General Manager */}
          <div className="executives-row-top scroll-fade-up">
            {executives.slice(0, 2).map((executive, index) => (
              <div className="executive-card" key={index}>
                <div className="executive-image">
                  <img src={executive.image} alt={executive.name} />
                </div>
                <div className="executive-info">
                  <h4>{executive.name}</h4>
                  <span className="executive-position">{executive.position}</span>
                </div>
              </div>
            ))}
          </div>
          {/* Bottom Row - Other Officers */}
          <div className="executives-row-bottom scroll-fade-up">
            {executives.slice(2).map((executive, index) => (
              <div className="executive-card" key={index}>
                <div className="executive-image">
                  <img src={executive.image} alt={executive.name} />
                </div>
                <div className="executive-info">
                  <h4>{executive.name}</h4>
                  <span className="executive-position">{executive.position}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section">
        <div className="container">
          <div className="section-header scroll-fade-up">
            <h2 className="section-title">Why Choose MEDC</h2>
            <p className="section-subtitle">
              CDA Registered | Est. 1997 | 600+ Personnel
            </p>
          </div>
          <div className="values-grid scroll-fade-up">
            <div className="value-card">
              <div className="value-icon"><FiAward /></div>
              <h4>CDA Registered</h4>
              <p>Duly registered cooperative (Reg. No. 6520-11009760, CIN-0105110096).</p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FiStar /></div>
              <h4>Decade of Experience</h4>
              <p>More than a decade of supervision experience in the industry.</p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FiTrendingUp /></div>
              <h4>Regional Coverage</h4>
              <p>About 600 personnel across Luzon, Visayas, and Mindanao.</p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FiHeart /></div>
              <h4>Pro-Employee</h4>
              <p>Giving what is due to employees with complete benefits.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default About;

